import { z } from 'zod'

/**
 * Either an absolute http(s) URL, or a same-origin path such as `/api/v1` so the
 * Vite dev proxy can serve the API without a CORS preflight.
 */
const apiBaseUrl = z
  .string()
  .min(1)
  .refine(
    (value) => value.startsWith('/') || /^https?:\/\//i.test(value),
    'must be an absolute http(s) URL or a path beginning with "/"',
  )
  // Trailing slashes would produce `//admin/products` once paths are appended.
  .transform((value) => value.replace(/\/+$/, ''))

/**
 * Firebase web configuration.
 *
 * Every value here is public by design — Firebase ships them in the page and identifies
 * the project rather than authorising anything. The API key in particular is not a
 * credential: it names the project, and access is decided by Firebase Auth plus this
 * store's own admin_users table. Treat them as configuration, never as secrets.
 */
const envSchema = z.object({
  VITE_API_BASE_URL: apiBaseUrl,
  VITE_STORE_NAME: z.string().min(1).default('Store'),

  VITE_FIREBASE_API_KEY: z.string().min(1),
  VITE_FIREBASE_AUTH_DOMAIN: z.string().min(1),
  VITE_FIREBASE_PROJECT_ID: z.string().min(1),
  VITE_FIREBASE_APP_ID: z.string().min(1),
  // Only needed by other Firebase products; optional so a project that uses Auth alone
  // does not have to invent values it will never use.
  VITE_FIREBASE_STORAGE_BUCKET: z.string().optional().default(''),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.string().optional().default(''),
})

const parsed = envSchema.safeParse(import.meta.env)

if (!parsed.success) {
  const detail = parsed.error.issues
    .map((issue) => `  • ${String(issue.path[0] ?? '(root)')} ${issue.message}`)
    .join('\n')

  // Fail at boot rather than at the first fetch: a misconfigured base URL otherwise
  // shows up as an unexplained network error deep inside a screen.
  throw new Error(
    `Invalid environment configuration:\n${detail}\n\nCopy .env.example to .env.local and fill in the missing values.`,
  )
}

export const env = {
  apiBaseUrl: parsed.data.VITE_API_BASE_URL,
  /** Placeholder store name — branding is not decided; this is env-driven on purpose. */
  storeName: parsed.data.VITE_STORE_NAME,
  firebase: {
    apiKey: parsed.data.VITE_FIREBASE_API_KEY,
    authDomain: parsed.data.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: parsed.data.VITE_FIREBASE_PROJECT_ID,
    appId: parsed.data.VITE_FIREBASE_APP_ID,
    storageBucket: parsed.data.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: parsed.data.VITE_FIREBASE_MESSAGING_SENDER_ID,
  },
  isProduction: import.meta.env.PROD,
  isDevelopment: import.meta.env.DEV,
} as const

export type Env = typeof env
